import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import {Colors} from '../constants/colors'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import ConfirmModal from '../components/ConfirmModal'
import AddCompanyRepresentativeModal from '../components/CompanyMonitor/CompanyRepresentative/AddCompanyRepresentativeModal'
import {useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import ManualCompanyRepresentativeList from '../components/CompanyMonitor/CompanyRepresentative/ManualCompanyRepresentativeList';
import CompanyReportChangeList from '../components/CompanyMonitor/CompanyReportChange/CompanyReportChangeList';
import CompanyReportChangeHandlingModal from '../components/CompanyMonitor/CompanyReportChange/CompanyReportChangeHandlingModal';
import Divider from '../components/Divider';
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {Alert} from "react-bootstrap";
import NavLink from '../components/NavLink';
import useCompanyMonitorActions from '../hooks/useCompanyMonitorActions';
import useRouterQuery from '../hooks/useRouterQuery';
import {
  AddRepresentativeModel,
  AmlRiskClass,
  CompanyReportChangeModel,
  ManualRepresentativeModel
} from '../redux/companyMonitorTypes';
import CompanyEventList from '../components/CompanyMonitor/CompanyEvent/CompanyEventList';
import CompanyReport from "../components/CompanyMonitor/CompanyReport";
import Button from '../components/Button';
import SetCompandAmlRiskClassificationModal from '../components/CompanyMonitor/SetCompandAmlRiskClassificationModal';
import useCompanyAmlActions from '../hooks/useCompanyAmlActions';

const CompanyMonitoring = () => {
  const styles = useStyles()
  const params = useParams<{ marketId: string, orgNumber: string }>()
  const {query} = useRouterQuery()
  const navigate = useNavigate();

  const [confirmDeleteRepresentativeModalVisible, setConfirmDeleteRepresentativeModalVisible] = useState(false)
  const [addRepresentativeModalVisible, setAddRepresentativeModalVisible] = useState(false)
  const [confirmRegenerateReportModalVisible, setConfirmRegenerateReportModalVisible] = useState(false)
  const [regeneratingReport, setRegeneratingReport] = useState(false)

  const [companyRepresentativeDeleteCandidate, setCompanyRepresentativeDeleteCandidate] = useState<ManualRepresentativeModel | null>(null)
  const [amlRiskClassModalVisible, setAmlRiskClassModalVisible] = useState(false)
  const [companyChangeHandlingModalVisible, setCompanyChangeHandlingModalVisible] = useState(false)
  const [companyChangeCommentModalVisible, setCompanyChangeCommentModalVisible] = useState(false)
  const [selectedCompanyChange, setSelectedCompanyChange] = useState<CompanyReportChangeModel | null>(null)

  const loading = useAppSelector(state => state.companyMonitor.loading)
  const details = useAppSelector(state => state.companyMonitor.details)
  const error = useAppSelector(state => state.companyMonitor.error)

  const {
    getDetailsForCompany,
    addRepresentative,
    deleteRepresentative,
    handleCompanyReportChange,
    commentCompanyReportChange,
    setAmlRiskClassification,
    regenerateReports
  } = useCompanyMonitorActions()

  useEffect(() => {
    const reportId = query.get('reportId') || undefined

    if (details === null ||
      details.company.marketId !== params.marketId ||
      details.company.companyRegistrationNumber !== params.orgNumber ||
      details.report?.id !== reportId) {
      if (params.marketId && params.orgNumber) 
        getDetailsForCompany(params.marketId, params.orgNumber, reportId)
    }
  }, [])

  const handleDeleteCompanyRepresentative = (representative: ManualRepresentativeModel) => {
    setCompanyRepresentativeDeleteCandidate(representative)
    setConfirmDeleteRepresentativeModalVisible(true)
  }

  const handleConfirmDeleteCompanyRepresentative = () => {
    deleteRepresentative(details?.company.id as string, companyRepresentativeDeleteCandidate?.id || '')
    setCompanyRepresentativeDeleteCandidate(null)
    setConfirmDeleteRepresentativeModalVisible(false)
  }

  const handleAddCompanyRepresentative = (representative: AddRepresentativeModel) => {
    addRepresentative(details?.company.id as string, representative)
    setAddRepresentativeModalVisible(false)
  }

  const handleCompanyChangeClicked = (companyChange: CompanyReportChangeModel) => {
    setSelectedCompanyChange(companyChange);
    setCompanyChangeHandlingModalVisible(true);
  }

  const onCompanyChangeHandled = (comment: string) => {
    handleCompanyReportChange(details?.company.id as string, selectedCompanyChange?.id as string, comment)
    setCompanyChangeHandlingModalVisible(false)
  }

  const commentCompanyChangeClicked = (companyChange: CompanyReportChangeModel) => {
    setSelectedCompanyChange(companyChange);
    setCompanyChangeCommentModalVisible(true);
  }

  const onCompanyChangeComment = (comment: string) => {
    commentCompanyReportChange(details?.company.id as string, selectedCompanyChange?.id as string, comment)
    setCompanyChangeCommentModalVisible(false)
  }

  const onReportSelected = (id: string) => {
    if (details) {
      navigate(`/companymonitor/${details?.company.marketId}/${details?.company.companyRegistrationNumber}?reportId=${id}`)
      getDetailsForCompany(details?.company.marketId, details?.company.companyRegistrationNumber, id)
    }
  }

  const handleConfirmRegenerateReport = () => {
    setConfirmRegenerateReportModalVisible(false)

    if (!details) {
      return
    }

    const {id, marketId, companyRegistrationNumber} = details.company

    setRegeneratingReport(true)
    regenerateReports(id)
      .then(succeeded => {
        setRegeneratingReport(false)

        // The response carries the newly created report, so drop any reportId in the
        // url that still points at the previously selected historical report.
        if (succeeded) {
          navigate(`/companymonitor/${marketId}/${companyRegistrationNumber}`, {replace: true})
        }
      })
  }

  const onAmlRiskClassificationSet = (riskClass: AmlRiskClass) => {
    if (details) {
      setAmlRiskClassification(details.company.id, riskClass)
    }
    setAmlRiskClassModalVisible(false)
  }

  const renderAmlRiskClassification = () => {
    let color = Colors.green

    switch (details?.company.amlRiskClassification ?? AmlRiskClass.unacceptable) {
      case AmlRiskClass.unacceptable:
        color = Colors.rubel_900
        break
      case AmlRiskClass.high:
        color = 'orange'
        break
      case AmlRiskClass.normal:
      case AmlRiskClass.low:
        color = Colors.green
        break
    }

    return (
      <p>AML risk classification: <span style={{color: color}}>{details?.company.amlRiskClassification ?? 'Not set'}</span></p>
    )
  }

  return (
    <div className={styles.container}>
      <h1>{details?.company.name} ({details?.company.companyRegistrationNumber})</h1>
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)} />
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>
          <Button
            title='Regenerate report'
            onClick={() => setConfirmRegenerateReportModalVisible(true)}
            disabled={!details}
          />
          <Button
            title='Set AML risk classification'
            onClick={() => setAmlRiskClassModalVisible(true)} 
          />
        </div>
      </div>

      {error && <Alert variant='danger' className={styles.errorAlert}>{String(error)}</Alert>}

      <CardSection>
        <DetailsCard>
          <h6>Company</h6>
          {renderAmlRiskClassification()}
        </DetailsCard>
      </CardSection>

      {details?.report &&
        <CardSection>
          <DetailsCard>
            <CompanyReport
              report={details.report}
              company={details.company}
              reportHistory={details.companyReportHistory}
              onReportSelected={onReportSelected}
            />
          </DetailsCard>
        </CardSection>
      }

      <CardSection>
        <DetailsCard iconType='Add' onClick={() => setAddRepresentativeModalVisible(true)}>
          <h6>Manually handled representatives</h6>
          <ManualCompanyRepresentativeList
            representatives={details?.manuallyHandledRepresentatives ?? []}
            onDeleteClick={(companyRep) => handleDeleteCompanyRepresentative(companyRep)}
          />
        </DetailsCard>
      </CardSection>

      <Divider height={50}/>

      <CardSection>
        <DetailsCard>
          <h6>Change handling changes</h6>
          <CompanyReportChangeList
            enableHandling={true}
            changes={details?.changeHandlingChanges ?? []}
            onHandleClick={handleCompanyChangeClicked}
            onCommentClick={commentCompanyChangeClicked}
          />
        </DetailsCard>
      </CardSection>

      <CardSection>
        <DetailsCard>
          <h6>Company events</h6>
          <CompanyEventList companyEvents={details?.events ?? []}/>
        </DetailsCard>
      </CardSection>

      <Divider height={50}/>
      <ConfirmModal
        visible={confirmDeleteRepresentativeModalVisible}
        confirmText={`Are you sure you want to remove ${companyRepresentativeDeleteCandidate?.givenName} ${companyRepresentativeDeleteCandidate?.familyName}?`}
        onConfirmClick={handleConfirmDeleteCompanyRepresentative}
        onCancelClick={() => setConfirmDeleteRepresentativeModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmRegenerateReportModalVisible}
        confirmText='Regenerating fetches fresh data from CreditSafe and Pliance and always adds a new report to the history. Continue?'
        onConfirmClick={handleConfirmRegenerateReport}
        onCancelClick={() => setConfirmRegenerateReportModalVisible(false)}
      />
      <SetCompandAmlRiskClassificationModal 
        intitialValue={details?.company.amlRiskClassification ?? AmlRiskClass.low}
        visible={amlRiskClassModalVisible}
        onSaveClick={(riskClass: AmlRiskClass) => onAmlRiskClassificationSet(riskClass)}
        onHideClick={() => setAmlRiskClassModalVisible(false)}
        onCancelClick={() => setAmlRiskClassModalVisible(false)}
      />
      <AddCompanyRepresentativeModal
        visible={addRepresentativeModalVisible}
        onAddClick={handleAddCompanyRepresentative}
        onCancelClick={() => setAddRepresentativeModalVisible(false)}
      />
      <CompanyReportChangeHandlingModal
        title='Handle change'
        visible={companyChangeHandlingModalVisible}
        companyChangeId={selectedCompanyChange?.id}
        onHideClick={() => setCompanyChangeHandlingModalVisible(false)}
        onSaveClick={onCompanyChangeHandled}
        onCancelClick={() => setCompanyChangeHandlingModalVisible(false)}
      />
      <CompanyReportChangeHandlingModal
        title='Comment'
        visible={companyChangeCommentModalVisible}
        companyChangeId={selectedCompanyChange?.id}
        onHideClick={() => setCompanyChangeCommentModalVisible(false)}
        onSaveClick={onCompanyChangeComment}
        onCancelClick={() => setCompanyChangeCommentModalVisible(false)}
      />
      <FullScreenLoadingIndicator
        visible={loading}
        message={regeneratingReport ? 'Regenerating report. This can take up to a minute.' : undefined}
      />
    </div>
  )
}

export default CompanyMonitoring

const useStyles = createUseStyles({
  container: {
    '& h5': {
      fontFamily: 'Aestetico-Regular',
      color: Colors.darkDefault,
    }
  },
  actionBar: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  errorAlert: {
    marginTop: 20
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%'
  },
  reportContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10
  },
  largeButton: {
    fontSize: "xxx-large",
    alignSelf: "center",
    margin: "20px",
    flexGrow: 1,
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  },
  icon: {
    marginRight: "5px",
  }
})
